import React, { useState } from 'react';
import { Switch, Tooltip, Modal, message } from 'antd';
import { setScheduleSensor } from '../services/dataPlatformService';

/**
 * Per-connector scheduler switch, styled as a single pill:
 * `( ● Scheduler  ●— )` — a bordered capsule containing a status dot, the
 * word "Scheduler", and a mini toggle switch.
 *
 * Scheduling in Dagster is driven by one `{connector}_schedule_sensor` per
 * connector (shared by all of that connector's streams), so this control is
 * keyed by connector — not connection or stream. Turning it on enables the
 * connector's cron schedules; turning it off pauses them (with confirm).
 *
 * Props:
 *   • connector       — connector key in underscored form (use connectorSensorKey()).
 *   • sensor          — matching getSchedulerStatus().sensors entry, or null/undefined.
 *   • daemonHealthy   — whether the SENSOR daemon is up (affects the dot colour).
 *   • onChanged(next) — called with the refreshed scheduler-status payload.
 *   • size            — 'small' (default) or 'middle'/'default' for a larger switch.
 *   • showLabel       — render the "Scheduler" word (default true).
 */
/** Status dot colour — grey (unknown), amber (off), green/orange (on,
 * healthy/unhealthy daemon). A sequence of ifs rather than nested ternaries
 * so each case reads on its own line. */
export function resolveDotColor(known, on, daemonHealthy) {
  if (!known) {return '#d9d9d9';}
  if (!on) {return '#bfbfbf';}
  return daemonHealthy ? '#52c41a' : '#faad14';
}

export function resolveSchedulerTip(known, on, daemonHealthy) {
  if (!known) {return 'No schedule sensor found for this connector';}
  if (!on) {return 'Scheduling is off — cron schedules will not fire until started';}
  return daemonHealthy
    ? 'Scheduling is on — cron schedules will fire'
    : 'Sensor enabled, but the Dagster SENSOR daemon is down — runs will not fire';
}

export function toggleSuccessMessage(action, connector) {
  return `Scheduler ${action === 'start' ? 'started' : 'stopped'} for ${connector}.`;
}

export default function SchedulerControl({
  connector, sensor, daemonHealthy = true, onChanged, size = 'small', showLabel = true,
}) {
  const [busy, setBusy] = useState(false);
  const known = !!sensor;
  const on = (sensor && sensor.status) === 'RUNNING';

  const doToggle = async (action) => {
    setBusy(true);
    try {
      const next = await setScheduleSensor(connector, action);
      if (onChanged) {onChanged(next);}
      message.success(toggleSuccessMessage(action, connector));
    } catch (err) {
      message.error(`Failed to ${action} scheduler: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onChange = (next) => {
    if (next) {
      doToggle('start');
    } else {
      // Confirm before pausing — turning the sensor off stops all of this
      // connector's cron schedules.
      Modal.confirm({
        title: 'Stop scheduling for this connector?',
        content: 'Its cron schedules will stop firing until re-enabled.',
        okText: 'Stop',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => doToggle('stop'),
      });
    }
  };

  const dotColor = resolveDotColor(known, on, daemonHealthy);
  const tip = resolveSchedulerTip(known, on, daemonHealthy);
  const switchSize = size === 'small' ? 'small' : 'medium';

  return (
    <Tooltip title={tip}>
      <span
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          height: switchSize === 'small' ? 28 : 32,
          padding: '0 8px 0 11px',
          border: '1px solid var(--theme-border, #e8e8e8)',
          borderRadius: 16,
          background: 'var(--theme-contentBg, #fff)',
          opacity: known ? 1 : 0.6,
          cursor: known ? 'default' : 'not-allowed',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{
          width: 8, height: 8, borderRadius: '50%', background: dotColor, flex: 'none',
        }}
        />
        {showLabel && (
          <span style={{ fontSize: 12.5, color: on ? 'var(--theme-textSecondary, #595959)' : 'var(--theme-textIcon, #8c8c8c)' }}>Scheduler</span>
        )}
        <Switch
          size={switchSize}
          checked={on}
          loading={busy}
          disabled={!known || busy}
          onChange={onChange}
        />
      </span>
    </Tooltip>
  );
}

/**
 * Map a connection's `connector_name` (possibly hyphenated, e.g.
 * "github-copilot") to the sensor key used by getSchedulerStatus().sensors
 * and accepted by setScheduleSensor() — the underscored code-location form
 * ("github_copilot"). Mirrors `_location_name` in pipeline-api.
 */
export function connectorSensorKey(connectorName) {
  return (connectorName || '').replace(/-/g, '_');
}
